'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Icon, Text, useTranslation } from '@weareconceptstudio/core';

import { buildAddressFromGeocode } from '@/utils/addressFormatting';
import AddressMapFieldStyle from './style';
import { formatAmd, getCartSubtotal, resolveDeliveryPricing } from './resolveDeliveryPricing';

const geocodeApiKey = process.env.APP_YANDEX_GEOCODE_API_KEY;

const DEFAULT_CENTER = [40.1872, 44.5152];
const DEFAULT_ZOOM = 16;
const DETAIL_ZOOM = 16;
const GEOCODE_MIN_DISTANCE = 0.0007;

const waitForYmaps = () =>
	new Promise((resolve) => {
		const check = () => (window.ymaps ? resolve(window.ymaps) : setTimeout(check, 100));
		check();
	});

const AddressMapField = () => {
	const { setValue, control, setError, clearErrors } = useFormContext();
	const { selectedLang, translate } = useTranslation();
	const outsideZoneError = translate('addressMapOutsideZone');
	const [errorMsg, setErrorMsg] = useState(null);
	const [outsideZone, setOutsideZone] = useState(false);
	const [isLocating, setIsLocating] = useState(false);
	const [isMapLoading, setIsMapLoading] = useState(true);
	const [pricingLoading, setPricingLoading] = useState(false);
	const [deliveryPricing, setDeliveryPricing] = useState(null);
	const errorTimerRef = useRef(null);
	const userLocationRef = useRef(null);

	const lat = useWatch({ control, name: 'geo_coords.lat' });
	const lon = useWatch({ control, name: 'geo_coords.lon' });

	const mapRef = useRef(null);
	const mapInstanceRef = useRef(null);
	const placemarkRef = useRef(null);
	const userLocationPlacemarkRef = useRef(null);

	const lastGeocodeRef = useRef(null);
	const isInternalUpdateRef = useRef(false);
	const isDestroyedRef = useRef(false);
	const yandexLangRef = useRef('hy_AM');

	yandexLangRef.current = selectedLang === 'hy' ? 'hy_AM' : selectedLang === 'ru' ? 'ru_RU' : 'en_US';

	const reverseGeocode = (fLat, fLon) => {
		if (!geocodeApiKey) return;

		if (lastGeocodeRef.current) {
			const [pLat, pLon] = lastGeocodeRef.current;
			if (Math.abs(pLat - fLat) + Math.abs(pLon - fLon) < GEOCODE_MIN_DISTANCE) return;
		}
		lastGeocodeRef.current = [fLat, fLon];

		fetch(
			`https://geocode-maps.yandex.ru/v1/?apikey=${geocodeApiKey}&geocode=${fLon},${fLat}&format=json&results=1&lang=${yandexLangRef.current}`
		)
			.then((r) => r.json())
			.then((data) => {
				const geoObj = data?.response?.GeoObjectCollection?.featureMember?.[0]?.GeoObject;
				if (!geoObj) return;

				setValue('address', buildAddressFromGeocode(geoObj));
			})
			.catch(() => {});
	};

	const showError = (msg) => {
		setErrorMsg(msg);
		if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
		errorTimerRef.current = setTimeout(() => setErrorMsg(null), 3000);
	};

	const placeMarkerRef = useRef(null);
	const requestUserLocationRef = useRef(null);
	const fetchDeliveryPricingRef = useRef(null);

	const fetchDeliveryPricing = useCallback(
		async (fLat, fLon) => {
			setPricingLoading(true);
			try {
				const result = await resolveDeliveryPricing({
					lat: fLat,
					lon: fLon,
					subtotal: getCartSubtotal(),
					lang: selectedLang,
				});

				if (!result?.available) {
					setDeliveryPricing(null);
					setOutsideZone(true);
					setError('geo_coords', { type: 'manual', message: 'outsideZone' });
					setError('address', { type: 'manual', message: outsideZoneError });
					return null;
				}

				setOutsideZone(false);
				clearErrors('geo_coords');
				clearErrors('address');
				setDeliveryPricing(result);
				return result;
			} catch {
				setDeliveryPricing(null);
				setOutsideZone(false);
				return null;
			} finally {
				setPricingLoading(false);
			}
		},
		[selectedLang]
	);

	fetchDeliveryPricingRef.current = fetchDeliveryPricing;

	const placeMarker = useCallback(
		async (fLat, fLon) => {
			if (!mapInstanceRef.current || !placemarkRef.current) return;

			placemarkRef.current.geometry.setCoordinates([fLat, fLon]);
			const currentZoom = mapInstanceRef.current.getZoom();
			mapInstanceRef.current.setCenter([fLat, fLon], Math.max(currentZoom, DETAIL_ZOOM), { duration: 300 });

			const pricing = await fetchDeliveryPricing(fLat, fLon);

			reverseGeocode(fLat, fLon);

			if (!pricing) {
				isInternalUpdateRef.current = true;
				setValue('geo_coords', null);
				requestAnimationFrame(() => {
					isInternalUpdateRef.current = false;
				});
				return;
			}

			isInternalUpdateRef.current = true;
			setValue('geo_coords', { lat: fLat, lon: fLon });
			requestAnimationFrame(() => {
				isInternalUpdateRef.current = false;
			});
		},
		[fetchDeliveryPricing, outsideZoneError, setValue]
	);

	const updateUserLocationMarker = useCallback((fLat, fLon) => {
		const ymaps = window.ymaps;
		if (!ymaps || !mapInstanceRef.current) return;

		userLocationRef.current = [fLat, fLon];
		const coords = [fLat, fLon];

		if (userLocationPlacemarkRef.current) {
			userLocationPlacemarkRef.current.geometry.setCoordinates(coords);
			return;
		}

		const marker = new ymaps.Placemark(
			coords,
			{},
			{
				preset: 'islands#blueCircleDotIcon',
				draggable: false,
				zIndex: 3000,
			}
		);
		mapInstanceRef.current.geoObjects.add(marker);
		userLocationPlacemarkRef.current = marker;
	}, []);

	const requestUserLocation = useCallback(
		({ silent = false, movePin = false } = {}) => {
			if (!navigator.geolocation) {
				if (!silent) showError(translate('geolocationNotSupported'));
				return;
			}

			setIsLocating(true);

			navigator.geolocation.getCurrentPosition(
				({ coords }) => {
					setIsLocating(false);
					const fLat = coords.latitude;
					const fLon = coords.longitude;
					updateUserLocationMarker(fLat, fLon);

					if (movePin) {
						placeMarker(fLat, fLon);
					} else if (mapInstanceRef.current) {
						mapInstanceRef.current.setCenter([fLat, fLon], DETAIL_ZOOM, { duration: 300 });
					}
				},
				() => {
					setIsLocating(false);
					if (!silent) showError(translate('unableToGetLocation'));
				},
				{ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
			);
		},
		[placeMarker, updateUserLocationMarker]
	);

	placeMarkerRef.current = placeMarker;
	requestUserLocationRef.current = requestUserLocation;

	useEffect(() => {
		if (typeof window === 'undefined' || !mapRef.current) return;
		isDestroyedRef.current = false;

		(async () => {
			try {
				const ymaps = await waitForYmaps();
				await ymaps.ready();

				if (isDestroyedRef.current || !mapRef.current || mapInstanceRef.current) return;

				const initialCenter = lat != null && lon != null ? [lat, lon] : DEFAULT_CENTER;

				const map = new ymaps.Map(
					mapRef.current,
					{
						center: initialCenter,
						zoom: lat != null ? DETAIL_ZOOM : DEFAULT_ZOOM,
						controls: [],
					},
					{
						suppressMapOpenBlock: true,
						suppressObsoleteBrowserNotifier: true,
					}
				);

				map.controls.add('zoomControl', { position: { right: 10, top: 10 } });

				const PinLayout = ymaps.templateLayoutFactory.createClass(
					'	<div class="murakami-gps-container"><i class="murakami-location"></i></div>'
				);

				const placemark = new ymaps.Placemark(
					initialCenter,
					{},
					{
						iconLayout: PinLayout,
						iconShape: { type: 'Circle', coordinates: [0, -18], radius: 18 },
						draggable: true,
					}
				);

				placemark.events.add('dragend', () => {
					const coords = placemark.geometry.getCoordinates();
					placeMarkerRef.current?.(coords[0], coords[1]);
				});

				map.events.add('click', (e) => {
					const coords = e.get('coords');
					placeMarkerRef.current?.(coords[0], coords[1]);
				});

				map.geoObjects.add(placemark);

				mapInstanceRef.current = map;
				placemarkRef.current = placemark;
				setIsMapLoading(false);

				if (lat == null || lon == null) {
					requestUserLocationRef.current?.({ silent: true, movePin: true });
				} else {
					fetchDeliveryPricingRef.current?.(lat, lon);
				}
			} catch (e) {
				console.error('Yandex Maps init error:', e);
				setIsMapLoading(false);
			}
		})();

		return () => {
			isDestroyedRef.current = true;
			if (mapInstanceRef.current) {
				mapInstanceRef.current.destroy();
				mapInstanceRef.current = null;
				placemarkRef.current = null;
				userLocationPlacemarkRef.current = null;
			}
		};
	}, []);

	const coordsKey = lat != null ? `${lat},${lon}` : null;
	useEffect(() => {
		if (isInternalUpdateRef.current) return;
		if (!mapInstanceRef.current || !placemarkRef.current) return;

		if (lat != null && lon != null) {
			placemarkRef.current.geometry.setCoordinates([lat, lon]);
			mapInstanceRef.current.setCenter([lat, lon], DETAIL_ZOOM, { duration: 300 });
			fetchDeliveryPricing(lat, lon);
		} else {
			setDeliveryPricing(null);
			setOutsideZone(false);
			clearErrors('geo_coords');
			clearErrors('address');
			placemarkRef.current.geometry.setCoordinates(DEFAULT_CENTER);
			mapInstanceRef.current.setCenter(DEFAULT_CENTER, DEFAULT_ZOOM, { duration: 300 });
		}
	}, [coordsKey, fetchDeliveryPricing, lat, lon]);

	const pricingText =
		deliveryPricing?.shipping_cost === 0
			? translate('addressMapDeliveryFree')
			: translate('addressMapDeliveryPrice', {
					price: formatAmd(deliveryPricing?.shipping_cost),
				});

	return (
		<AddressMapFieldStyle>
			<div
				ref={mapRef}
				className='map-container'
			/>
			{pricingLoading && (
				<div className='map-info-chip chip-delivery'>
					<div className='delivery-pricing-loading' />
				</div>
			)}
			{!pricingLoading && deliveryPricing && (
				<div className='map-info-chip chip-delivery'>
					<div className='chip-row1'>
						<Text className='p p5 font-montserrat-arm-medium chip-label'>{pricingText}</Text>
					</div>
					{deliveryPricing.free_shipping_threshold && deliveryPricing.shipping_cost > 0 ? (
						<Text className='p p5 font-montserrat-arm-regular chip-note'>
							{translate('addressMapFreeShippingFrom', {
								price: formatAmd(deliveryPricing.free_shipping_threshold),
							})}
						</Text>
					) : null}
				</div>
			)}
			{!pricingLoading && outsideZone && (
				<div className='map-info-chip chip-outside'>
					<div className='chip-row1'>
						<Icon
							name='warning'
							className='chip-icon-warning'
						/>
						<Text className='p p5 font-montserrat-arm-medium  chip-label background-color'>
							{outsideZoneError}
						</Text>
					</div>
				</div>
			)}
			{isMapLoading && (
				<div className='map-loading'>
					<div className='map-spinner' />
				</div>
			)}
			<button
				type='button'
				className='locate-button'
				onClick={() => requestUserLocation({ movePin: true })}
				disabled={isLocating}
				aria-label={translate('myLocationAriaLabel')}>
				<Icon
					name='gps'
					className='locate-icon'
				/>
			</button>
			{errorMsg && (
				<div className='error-toast'>
					<Icon
						name='warning'
						className='error-icon'
					/>
					<Text className='p p5 font-montserrat-arm-regular background-color'>{errorMsg}</Text>
				</div>
			)}
		</AddressMapFieldStyle>
	);
};

export default AddressMapField;
